<template>
	<div
		id="speechkit-io-iframe"
		ref="playerTarget"
		class="beyond-words-embed mb-[24px]"
		style="min-width: 280px; display: block; width: 100%; min-height: 60px"
	/>
</template>

<script setup>
import { BEYONDWORDS_PROJECT_ID } from "~/utils/beyondwords.js";

const props = defineProps({
	beyondwords: {
		type: [String, Number],
		required: true
	}
});

const playerTarget = ref(null);
let playerInstance = null;
let scriptPromise = null;

const loadBeyondWordsPlayerScript = () => {
	if (import.meta.server) {
		return Promise.resolve();
	}

	if (window.BeyondWords?.Player) {
		return Promise.resolve();
	}

	if (scriptPromise) {
		return scriptPromise;
	}

	scriptPromise = new Promise((resolve, reject) => {
		const existing = document.querySelector(
			'script[data-beyondwords-player-sdk="true"]'
		);

		if (existing) {
			existing.addEventListener("load", () => resolve(), { once: true });
			existing.addEventListener("error", reject, { once: true });
			return;
		}

		const script = document.createElement("script");
		script.async = true;
		script.defer = true;
		script.dataset.beyondwordsPlayerSdk = "true";
		script.src =
			"https://proxy.beyondwords.io/npm/@beyondwords/player@latest/dist/umd.js";
		script.onload = () => resolve();
		script.onerror = reject;
		document.head.appendChild(script);
	});

	return scriptPromise;
};

const destroyPlayer = () => {
	if (playerInstance?.destroy) {
		playerInstance.destroy();
	}
	playerInstance = null;
};

const initPlayer = async () => {
	if (!props.beyondwords || !playerTarget.value || import.meta.server) {
		return;
	}

	try {
		await loadBeyondWordsPlayerScript();

		if (!window.BeyondWords?.Player) {
			console.log("ERROR ON BEYOND WORDS PLAYER: SDK unavailable");
			return;
		}

		destroyPlayer();

		playerInstance = new window.BeyondWords.Player({
			target: playerTarget.value,
			projectId: Number(BEYONDWORDS_PROJECT_ID),
			contentId: String(props.beyondwords),
			playerStyle: "standard",
			// Hide the floating mini-player that appears when scrolling past
			// the inline embed. Per BeyondWords player properties docs:
			// widgetStyle "none" disables the sticky bottom widget.
			// Constructor props win over project dashboard settings.
			widgetStyle: "none",
			// Disable segment hover/click behavior so scrolling doesn't
			// highlight paragraphs or start playback from the article body.
			highlightSections: "all-none",
			clickableSections: "none",
			segmentWidgetSections: "none"
		});
	} catch (e) {
		console.log("ERROR ON BEYOND WORDS PLAYER", e);
	}
};

onMounted(() => {
	initPlayer();
});

onBeforeUnmount(() => {
	destroyPlayer();
});

watch(
	() => props.beyondwords,
	() => {
		initPlayer();
	}
);
</script>
